/**
 * SiteContextInput — Site_Context SC5/SC6.
 *
 * Shared component for two surfaces:
 *   - New Site wizard (SC5): no `siteId`, the input is uncontrolled and
 *     the parent owns the text + extraction.
 *   - Rerun wizard (SC6): `siteId` is set, edit-mode toggle exposed,
 *     the parent decides whether changes persist to the site or live
 *     as a one-shot rerun-override.
 *
 * Visual treatment is deliberately primary-bordered and non-uppercase —
 * the spec calls for a prominent panel that breaks the platform's
 * usual tracking-widest pattern so first-time users actually read it.
 *
 * Extraction runs onBlur via the parent's callback (typically wired to
 * the SC2 `updateSiteContext` action or, for the new-site flow, an
 * in-memory cache that flushes on createSite). Conflicts surface
 * inline below the textarea via SiteContextConflicts (SC7).
 */
import React, { useRef, useState, useCallback } from 'react';
import { Sparkles } from 'lucide-react';
import type { SiteContext, ExtractedClaim } from '../../types/siteContext';
import { SITE_CONTEXT_MAX_CHARS, SITE_CONTEXT_WARN_CHARS } from '../../types/siteContext';
import { SiteContextConflicts } from './SiteContextConflicts';
import { SceQualityChip } from './SceQualityChip';
import { promoteClaimToUserAuthored } from '../../services/siteContext/authoredEvidenceWriter';

/**
 * Local draft for the inline claim editor. We hold the value as a STRING
 * for the text/number inputs (so the user can transiently type "8." or
 * clear the field) plus the original `valueKind` so Save can coerce back
 * to the right primitive. Booleans hold a real `boolean`.
 */
interface ClaimEditDraft {
  text: string;
  category: ExtractedClaim['category'];
  /** Which control to render + how to coerce on Save. */
  valueKind: 'number' | 'boolean' | 'string' | 'undefined';
  /** String-backed value for number/string inputs. */
  valueStr: string;
  /** Boolean-backed value for the boolean toggle. */
  valueBool: boolean;
}

export type SiteContextEditMode = 'edit-site' | 'rerun-override';

export interface SiteContextInputProps {
  /** Current freeform text. Parent owns. */
  value: string;
  onChange: (v: string) => void;
  /** Latest snapshot (claims + conflicts) — populated by the parent
   *  via the extractor. Undefined ⇒ extraction hasn't run yet. */
  context?: SiteContext;
  /** Called when the user blurs the textarea (and text has changed
   *  since the last extraction). Parent runs the extractor + cross-
   *  checker and updates `context`. */
  onBlurExtract?: (text: string) => void | Promise<void>;
  /** True while the extractor is running. */
  extractionStatus?: 'idle' | 'running' | 'error';
  /** Acknowledge a conflict (drops it from the live warning list). */
  onAcknowledge?: (conflictId: string) => void;
  /** Optional edit-mode toggle for the rerun surface (SC6).
   *  Omit for the new-site surface (SC5). */
  mode?: SiteContextEditMode;
  onModeChange?: (m: SiteContextEditMode) => void;
  /** Test-id prefix for the regression suite. */
  testIdPrefix?: string;
  /** Optional label override; defaults to "What we know about this site". */
  label?: string;
  /**
   * IC9 — optional handler for direct claim manipulation. When passed,
   * the structured claim list below the prose surfaces a [Delete]
   * action per row; clicking it removes the claim from the context and
   * fires this callback with the trimmed list. Absent ⇒ read-only.
   */
  onClaimsChange?: (next: ExtractedClaim[]) => void;
  /**
   * CLP-2 — optional retry handler for the "Re-extract claims" button
   * and the inline chip retry affordance. When passed, the panel
   * renders a [ Re-extract claims ] button beneath the textarea that
   * fires this callback; the chip also renders an inline `Retry` link
   * on error states. Absent ⇒ no retry UI (e.g. the new-site wizard
   * surface where the site doesn't exist yet and `retryExtractClaims`
   * has no target).
   *
   * Callers wire this to `retryExtractClaims(siteId)` (the SiteContext
   * tab) or to a local in-memory re-run (the rerun panel).
   */
  onRetryExtract?: () => void | Promise<void>;
  /**
   * Declutter (2026-06-10) — when true, render ONLY the prose textarea
   * surface (label + helper + examples toggle + textarea + char count).
   * Extraction STILL runs SILENTLY via `onBlurExtract`; we just hide the
   * structured-claim list, the per-claim inline editor, the "Re-extract
   * claims" button and the conflict notices so the surface reads as a
   * single "what we know" prose field. Used by the Intent stepper's
   * Context step, where claims are evidence captured behind the scenes,
   * not a UI the user manages. Default false ⇒ existing callers (Site
   * Context tab, rerun panel) keep the full claim-management UI.
   */
  hideClaimList?: boolean;
}

const PLACEHOLDER = [
  '"BTM nuclear, NuScale SMR, 4×77MW modules, 24-month commercial-operation target."',
  '"Utility has committed to Q4 2026 substation upgrade — letter on file."',
  '"Phase I ESA complete (2025-11) — clean; no RECs identified."',
  '"Existing geotechnical from 2019 project applies — 4 borings to 50m."',
].join('\n');

const EXAMPLE_BULLETS = [
  '"BTM nuclear, NuScale SMR, 4×77MW modules, 24-month commercial-operation target"',
  '"Utility has committed to Q4 2026 substation upgrade — letter on file"',
  '"Phase I ESA complete (2025-11) — clean; no RECs identified"',
  '"Existing geotechnical from 2019 project applies — 4 borings to 50m"',
];

export const SiteContextInput: React.FC<SiteContextInputProps> = ({
  value,
  onChange,
  context,
  onBlurExtract,
  extractionStatus = 'idle',
  onAcknowledge,
  mode,
  onModeChange,
  testIdPrefix = 'site-context',
  label = 'What we know about this site',
  onClaimsChange,
  onRetryExtract,
  hideClaimList = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Track the last value we sent to the extractor so we don't re-fire
  // on a blur when the user didn't actually change anything.
  const [lastExtracted, setLastExtracted] = useState<string>(value);

  const handleBlur = useCallback(() => {
    if (!onBlurExtract) return;
    if (value === lastExtracted) return;
    setLastExtracted(value);
    void onBlurExtract(value);
  }, [onBlurExtract, value, lastExtracted]);

  const handleScrollToClaim = useCallback((sourceSpan: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const idx = value.indexOf(sourceSpan);
    el.focus();
    if (idx >= 0) {
      try {
        el.setSelectionRange(idx, idx + sourceSpan.length);
      } catch { /* setSelectionRange not available — focus alone is fine */ }
    }
  }, [value]);

  const claims = context?.claims ?? [];
  const conflicts = context?.conflicts ?? [];
  const acknowledgedConflictIds = context?.acknowledgedConflictIds ?? [];

  const overWarn = value.length > SITE_CONTEXT_WARN_CHARS;

  // RWX-7 — examples disclosure. Pre-RWX, the four EXAMPLE_BULLETS
  // rendered as a visible `<ul>` ABOVE the textarea (lines 130-134),
  // styled with quotation marks so users frequently misread them as
  // their own already-typed entries — then concluded the parser was
  // broken when they saw "0 claims parsed" (W9 from the 2026-05-17
  // walkthrough). Per pass-2 #6, the `placeholder` attribute on the
  // textarea is the correct affordance; the inline list was the
  // confusion. RWX-7 collapses the list behind a "Show examples"
  // toggle that defaults to HIDDEN so first-time users see only
  // the prose introduction + the placeholder ghost-text.
  const [examplesOpen, setExamplesOpen] = useState(false);

  // aurora-qa-3 — read/edit split. William: the always-on scroll textarea
  // crammed the prose into a small box; instead show the FULL content
  // READ-ONLY (full height, no inner scroll) with an explicit [Edit]
  // affordance that flips to the editable textarea. We start in EDIT mode
  // when there's nothing to read yet (the new-site wizard / empty field)
  // so first-time entry is friction-free, and READ-ONLY once content
  // exists. The onChange / onBlurExtract contract is unchanged — the
  // textarea is identical when shown.
  const [editing, setEditing] = useState<boolean>(() => value.trim().length === 0);
  const enterEdit = useCallback(() => setEditing(true), []);
  const exitEdit = useCallback(() => {
    setEditing(false);
    // Mirror the textarea blur so extraction runs when the user finishes
    // editing via the Done/Save button (not only via native blur).
    handleBlur();
  }, [handleBlur]);

  // Inline claim editor — one row editable at a time (keyed by claim id).
  // `null` ⇒ no row in edit mode (the read-only default).
  const [editingClaimId, setEditingClaimId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ClaimEditDraft | null>(null);

  // Resolve the value control type from the persisted primitive so the
  // user gets a number field for numeric values (LF 5.5 → 83.3), a
  // boolean toggle for booleans (flip an inverted "no permit filed"),
  // and a text field otherwise. `undefined` ⇒ no value set yet; we offer
  // a text field plus a "Set value" affordance via the same control.
  const valueKindOf = useCallback((v: ExtractedClaim['claimedValue']): ClaimEditDraft['valueKind'] => {
    if (typeof v === 'number') return 'number';
    if (typeof v === 'boolean') return 'boolean';
    if (typeof v === 'string') return 'string';
    return 'undefined';
  }, []);

  const beginEdit = useCallback((c: ExtractedClaim) => {
    const kind = valueKindOf(c.claimedValue);
    setEditingClaimId(c.id);
    setDraft({
      text: c.claim,
      category: c.category,
      valueKind: kind,
      valueStr: kind === 'boolean' || c.claimedValue === undefined ? '' : String(c.claimedValue),
      valueBool: typeof c.claimedValue === 'boolean' ? c.claimedValue : false,
    });
  }, [valueKindOf]);

  const cancelEdit = useCallback(() => {
    setEditingClaimId(null);
    setDraft(null);
  }, []);

  const saveEdit = useCallback((original: ExtractedClaim) => {
    if (!onClaimsChange || !draft) return;

    // Coerce the draft back to the primitive `claimedValue`:
    //   - boolean kind  ⇒ the toggle value.
    //   - number  kind  ⇒ parseFloat; blank / NaN ⇒ clear (null).
    //   - string  kind  ⇒ the trimmed string; blank ⇒ clear (null).
    //   - undefined kind⇒ if the user typed something, adopt it as a
    //                     string; else leave unset (undefined).
    let claimedValue: string | number | boolean | null | undefined;
    if (draft.valueKind === 'boolean') {
      claimedValue = draft.valueBool;
    } else if (draft.valueKind === 'number') {
      const trimmed = draft.valueStr.trim();
      if (trimmed === '') {
        claimedValue = null; // explicit clear
      } else {
        const n = Number(trimmed);
        claimedValue = Number.isFinite(n) ? n : null;
      }
    } else if (draft.valueKind === 'string') {
      const trimmed = draft.valueStr.trim();
      claimedValue = trimmed === '' ? null : trimmed;
    } else {
      // 'undefined' kind — only set if the user actually typed a value.
      const trimmed = draft.valueStr.trim();
      claimedValue = trimmed === '' ? undefined : trimmed;
    }

    const corrected = promoteClaimToUserAuthored({
      claim: original,
      claim_text: draft.text,
      claimedValue,
      category: draft.category,
    });

    onClaimsChange(claims.map(x => (x.id === original.id ? corrected : x)));
    setEditingClaimId(null);
    setDraft(null);
  }, [onClaimsChange, draft, claims]);

  return (
    <div
      className="border-2 border-primary/30 bg-primary/5 p-4 space-y-3"
      data-testid={`${testIdPrefix}-panel`}
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-text-primary">{label}</h3>
          <p className="text-xs text-text-secondary mt-1 leading-relaxed">
            Describe the site, technology, approach, commitments, prior studies — anything the AI should treat as ground truth.
            The narrative will cite this directly and weight it above regional-inference data.
          </p>
          <button
            type="button"
            onClick={() => setExamplesOpen(o => !o)}
            className="mt-1 text-[11px] underline text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            aria-expanded={examplesOpen}
            data-testid={`${testIdPrefix}-examples-toggle`}
          >
            {examplesOpen ? 'Hide examples' : 'Show examples'}
          </button>
          {examplesOpen && (
            <ul
              className="text-xs text-text-muted mt-1 space-y-0.5 pl-4 list-disc"
              data-testid={`${testIdPrefix}-examples-list`}
            >
              {EXAMPLE_BULLETS.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Edit-mode toggle — only rendered on the rerun surface (SC6).
          Default 'edit-site' is set by the caller (FeasibilityRerunPanel). */}
      {mode && onModeChange && (
        <div
          className="flex flex-col gap-1.5 border border-border bg-card p-2.5 text-xs"
          data-testid={`${testIdPrefix}-mode-toggle`}
        >
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              checked={mode === 'edit-site'}
              onChange={() => onModeChange('edit-site')}
              className="mt-0.5"
              data-testid={`${testIdPrefix}-mode-edit-site`}
            />
            <span>
              <span className="font-semibold text-text-primary">Edit site context (persists)</span>
              <span className="block text-text-secondary text-[11px] leading-snug">
                Changes apply to every future report on this site.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              checked={mode === 'rerun-override'}
              onChange={() => onModeChange('rerun-override')}
              className="mt-0.5"
              data-testid={`${testIdPrefix}-mode-rerun-override`}
            />
            <span>
              <span className="font-semibold text-text-primary">Override for this rerun only</span>
              <span className="block text-text-secondary text-[11px] leading-snug">
                Changes apply only to this report. Site context unchanged.
              </span>
            </span>
          </label>
        </div>
      )}

      {/* aurora-qa-3 — read/edit split. In EDIT mode the editable textarea
          renders exactly as before (same onChange/onBlur/maxLength contract).
          In READ-ONLY mode the full prose renders at full height (no inner
          scroll cap) so the user can read everything without a cramped box,
          with an [Edit] action top-right. Empty content shows a muted prompt
          + a primary "Add site context" button into edit mode. */}
      {editing ? (
        <div className="space-y-1.5">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value.slice(0, SITE_CONTEXT_MAX_CHARS))}
            onBlur={handleBlur}
            rows={8}
            maxLength={SITE_CONTEXT_MAX_CHARS}
            autoFocus={value.trim().length > 0}
            placeholder={`Type or paste anything important about this site...\n\n${PLACEHOLDER}`}
            className="w-full text-sm border border-border bg-card p-3 leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-primary font-mono resize-y"
            data-testid={`${testIdPrefix}-input`}
          />
          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={exitEdit}
              className="type-meta border border-primary text-primary bg-primary/5 px-2.5 py-1.5 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              data-testid={`${testIdPrefix}-done`}
            >
              Done
            </button>
          </div>
        </div>
      ) : value.trim().length > 0 ? (
        <div className="space-y-1.5" data-testid={`${testIdPrefix}-readonly`}>
          <div className="flex items-start justify-between gap-2">
            <div
              className="flex-1 min-w-0 text-sm border border-border bg-card p-3 leading-relaxed font-mono whitespace-pre-wrap break-words"
              data-testid={`${testIdPrefix}-readonly-content`}
            >
              {value}
            </div>
            <button
              type="button"
              onClick={enterEdit}
              className="type-meta border border-border bg-card px-2.5 py-1.5 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary shrink-0"
              data-testid={`${testIdPrefix}-edit`}
            >
              Edit
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 text-sm border border-dashed border-border bg-card p-3">
          <span className="text-text-muted">No site context yet.</span>
          <button
            type="button"
            onClick={enterEdit}
            className="type-meta border border-primary text-primary bg-primary/5 px-2.5 py-1.5 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary shrink-0"
            data-testid={`${testIdPrefix}-edit`}
          >
            Add site context
          </button>
        </div>
      )}

      {/*
        CLP-1 — status-aware chip. Before CLP, the only states were
        'running' / 'error' / 'idle' from the local `extractionStatus`
        prop — every failure looked identical (or, on the SiteContext
        tab, looked identical to "no claims found"). Now we read the
        persisted `context.extractorStatus` so the chip can distinguish
        quota-error / auth-error / parse-error / timeout / unknown-error
        from a genuine 0-claim parse. The local `extractionStatus`
        ('running' / 'error') still wins for in-flight extraction state
        because it covers the brief gap between the user blur and the
        store action returning the new status.
      */}
      <div className="flex items-center justify-between text-xs text-text-secondary">
        {/* Declutter — the claim-count / extraction-status readout is part
            of the claim-management surface. When `hideClaimList` is set the
            extraction runs SILENTLY, so we render a spacer (keeps the char
            counter right-aligned) instead of the status text. */}
        {hideClaimList ? <span /> : (() => {
          // Local prop wins for the in-flight running state.
          if (extractionStatus === 'running' || context?.extractorStatus === 'extracting') {
            return (
              <span data-testid={`${testIdPrefix}-status`}>Extracting claims…</span>
            );
          }
          const status = context?.extractorStatus;
          if (status === 'quota-error') {
            return (
              <span
                className="text-warning"
                data-testid={`${testIdPrefix}-status`}
                data-status="quota-error"
                title={context?.extractorError}
              >
                AI quota exhausted — claims not extracted.
                {onRetryExtract && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => { void onRetryExtract(); }}
                      className="underline text-warning hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
                      data-testid={`${testIdPrefix}-status-retry`}
                    >
                      Retry
                    </button>
                  </>
                )}
              </span>
            );
          }
          if (status === 'auth-error') {
            return (
              <span
                className="text-warning"
                data-testid={`${testIdPrefix}-status`}
                data-status="auth-error"
                title={context?.extractorError}
              >
                API key issue — claims not extracted. Contact admin.
              </span>
            );
          }
          if (status === 'timeout') {
            return (
              <span
                className="text-warning"
                data-testid={`${testIdPrefix}-status`}
                data-status="timeout"
                title={context?.extractorError}
              >
                Extraction timed out.
                {onRetryExtract && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => { void onRetryExtract(); }}
                      className="underline text-warning hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
                      data-testid={`${testIdPrefix}-status-retry`}
                    >
                      Retry
                    </button>
                  </>
                )}
              </span>
            );
          }
          if (status === 'parse-error' || status === 'unknown-error' || extractionStatus === 'error') {
            return (
              <span
                className="text-warning"
                data-testid={`${testIdPrefix}-status`}
                data-status={status ?? 'unknown-error'}
                title={context?.extractorError}
              >
                Extraction failed — the AI will still read your text verbatim.
                {onRetryExtract && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={() => { void onRetryExtract(); }}
                      className="underline text-warning hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning"
                      data-testid={`${testIdPrefix}-status-retry`}
                    >
                      Retry
                    </button>
                  </>
                )}
              </span>
            );
          }
          return (
            <span
              data-testid={`${testIdPrefix}-status`}
              data-status={status ?? 'idle'}
            >
              {claims.length} claim{claims.length === 1 ? '' : 's'} parsed
            </span>
          );
        })()}
        {/* aurora-qa-3 — the char counter belongs to the editing affordance;
            show it only in edit mode (William). A spacer keeps the status
            text left-aligned when the counter is hidden in read-only mode. */}
        {editing ? (
          <span
            className={overWarn ? 'text-warning' : ''}
            data-testid={`${testIdPrefix}-char-count`}
          >
            {value.length}/{SITE_CONTEXT_MAX_CHARS}
          </span>
        ) : (
          <span />
        )}
      </div>

      {/*
        CLP-2 — explicit "Re-extract claims" affordance. Lives below the
        status row so users can manually retry after a quota refresh /
        backend deploy, even when the text hasn't changed (the standard
        save flow would otherwise skip extraction). Disabled while an
        extraction is in flight to prevent double-spend.
      */}
      {!hideClaimList && onRetryExtract && value.trim().length > 0 && (
        <div className="flex items-center justify-end pt-1">
          <button
            type="button"
            onClick={() => { void onRetryExtract(); }}
            disabled={extractionStatus === 'running' || context?.extractorStatus === 'extracting'}
            className="type-meta border border-border bg-card px-2.5 py-1.5 hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid={`${testIdPrefix}-retry-extract`}
          >
            {extractionStatus === 'running' || context?.extractorStatus === 'extracting'
              ? 'Extracting…'
              : 'Re-extract claims'}
          </button>
        </div>
      )}

      {/* Declutter — conflict notices are part of the claim-management UI;
          hidden alongside the claim list when the surface is prose-only. */}
      {!hideClaimList && (
        <SiteContextConflicts
          claims={claims}
          conflicts={conflicts}
          acknowledgedConflictIds={acknowledgedConflictIds}
          onAcknowledge={onAcknowledge}
          onScrollToClaim={handleScrollToClaim}
          testIdPrefix={testIdPrefix}
        />
      )}

      {/* IC9 — structured claim list. Read-only by default; when an
          onClaimsChange callback is provided, each row exposes [Edit]
          and [Delete] actions. Edit puts the row into an inline editor
          that promotes the corrected claim to user-authored ground
          truth (see promoteClaimToUserAuthored). Hidden entirely when
          `hideClaimList` is set (silent-extraction prose surface). */}
      {!hideClaimList && claims.length > 0 && (
        <div
          className="border border-border bg-card p-2.5 space-y-1 text-xs"
          data-testid={`${testIdPrefix}-claim-list`}
        >
          <div className="type-meta text-text-secondary">
            Extracted claims ({claims.length})
          </div>
          {claims.map((c) => {
            const isEditing = onClaimsChange != null && editingClaimId === c.id && draft != null;
            return (
              <div
                key={c.id}
                className="border-t border-border/60 pt-1"
                data-testid={`${testIdPrefix}-claim-row-${c.id}`}
              >
                {isEditing ? (
                  <div
                    className="flex flex-col gap-1.5"
                    data-testid={`${testIdPrefix}-claim-editor-${c.id}`}
                  >
                    {/* Category — kept editable so a mis-filed claim can be
                        re-categorised. Free-text to avoid coupling the row
                        to the ClaimCategory union enumeration. */}
                    <label className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-text-secondary shrink-0 w-14">category</span>
                      <input
                        type="text"
                        value={draft.category}
                        onChange={(e) => setDraft(d => (d ? { ...d, category: e.target.value as ExtractedClaim['category'] } : d))}
                        className="flex-1 min-w-0 border border-border bg-card px-1.5 py-1 font-mono text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        data-testid={`${testIdPrefix}-claim-edit-category-${c.id}`}
                      />
                    </label>
                    {/* Claim text. */}
                    <label className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-text-secondary shrink-0 w-14">claim</span>
                      <input
                        type="text"
                        value={draft.text}
                        onChange={(e) => setDraft(d => (d ? { ...d, text: e.target.value } : d))}
                        className="flex-1 min-w-0 border border-border bg-card px-1.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        data-testid={`${testIdPrefix}-claim-edit-text-${c.id}`}
                      />
                    </label>
                    {/* Value — type-aware control. */}
                    <label className="flex items-center gap-1.5">
                      <span className="font-mono text-[10px] text-text-secondary shrink-0 w-14">value</span>
                      {draft.valueKind === 'boolean' ? (
                        <select
                          value={draft.valueBool ? 'true' : 'false'}
                          onChange={(e) => setDraft(d => (d ? { ...d, valueBool: e.target.value === 'true' } : d))}
                          className="flex-1 min-w-0 border border-border bg-card px-1.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          data-testid={`${testIdPrefix}-claim-edit-value-${c.id}`}
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : (
                        <input
                          type={draft.valueKind === 'number' ? 'number' : 'text'}
                          value={draft.valueStr}
                          onChange={(e) => setDraft(d => (d ? { ...d, valueStr: e.target.value } : d))}
                          placeholder={draft.valueKind === 'undefined' ? '(no value — type to set)' : ''}
                          className="flex-1 min-w-0 border border-border bg-card px-1.5 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          data-testid={`${testIdPrefix}-claim-edit-value-${c.id}`}
                        />
                      )}
                    </label>
                    <div className="flex items-center justify-end gap-2 pt-0.5">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="type-meta text-text-secondary hover:underline shrink-0"
                        data-testid={`${testIdPrefix}-claim-cancel-${c.id}`}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => saveEdit(c)}
                        className="type-meta text-primary font-semibold hover:underline shrink-0"
                        data-testid={`${testIdPrefix}-claim-save-${c.id}`}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-text-primary truncate flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[10px] text-text-secondary mr-1 shrink-0">{c.category}</span>
                      <span className="truncate">
                        {c.claim}
                        {c.claimedValue !== undefined && (
                          <span className="ml-1 text-text-muted">= {String(c.claimedValue)}</span>
                        )}
                      </span>
                      {/* SCE-FOLLOWUP-CLAIM-CHIPS — amber dot + click-to-disclose
                          when SCE-4's lazy migration (or a fresh SCE-2 extract)
                          stamped `qualityWarnings`. Renders nothing when
                          absent. The disclosure copy lives in SceQualityChip
                          so this row doesn't take a hard dep on the schema's
                          code namespace. */}
                      <SceQualityChip
                        warnings={c.qualityWarnings}
                        testIdPrefix={`${testIdPrefix}-claim-row-${c.id}`}
                      />
                    </span>
                    {onClaimsChange && (
                      <span className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => beginEdit(c)}
                          className="type-meta text-primary hover:underline shrink-0"
                          data-testid={`${testIdPrefix}-claim-edit-${c.id}`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => onClaimsChange(claims.filter(x => x.id !== c.id))}
                          className="type-meta text-danger hover:underline shrink-0"
                          data-testid={`${testIdPrefix}-claim-delete-${c.id}`}
                        >
                          Delete
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SiteContextInput;
